import { errorContainsStatusCode } from '@/util/error-contains-status-code';
import { HttpStatusCode } from 'axios';
import useGlobalToast from '@/composables/useGlobalToast';
import { getLogger } from '@/logger/logger';
import { getErrorCode } from '@/util/error.ts';
import type { Logger } from 'pino';
import {
    CompleteCartResponseErrorCodes,
    OfferCompletionResponseErrorCodes,
    RequestChangeResponseErrorCodes,
    UpdateCartResponseErrorCodes,
} from '@containex/portal-backend-dto';
import { ALLOWED_EXTENSIONS, ALLOWED_MIMES_EXT, MAX_FILE_SIZE_MB } from '@containex/portal-business-logic';

interface UseErrorHandler {
    errorHandlerFrom(option: Partial<ErrorHandlerOptions>): ErrorHandler;
}

interface ErrorHandler {
    handle(fn: () => Promise<void>): Promise<void>;
}

interface ErrorHandlerOptions {
    name: string;
    statusCodeHandlers: Record<number, (code: HttpStatusCode, error: unknown) => void>;
    handleInfoCodes: boolean;
    defaultHandler: (error: unknown) => void;
}
interface ToastMapping {
    summary: string;
    detail: string;
    summaryValues?: Record<string, string>;
    detailValues?: Record<string, string>;
}

export function useErrorHandler(): UseErrorHandler {
    return {
        errorHandlerFrom,
    };
}

function errorHandlerFrom(option: Partial<ErrorHandlerOptions>): ErrorHandler {
    const { errorToastOptions, addToast } = useGlobalToast();
    const logger = getLogger(option.name);

    return {
        async handle(fn: () => Promise<void>): Promise<void> {
            try {
                await fn();
            } catch (error: unknown) {
                const infoCode = getErrorCode(error);
                if (option.handleInfoCodes === true && infoCode != null) {
                    logger.warn(error);
                    displayInfoCodeToast(infoCode, logger);
                    return;
                }

                if (option.statusCodeHandlers != null && errorContainsStatusCode(error)) {
                    const handler = option.statusCodeHandlers[error.request.status];

                    if (handler != null) {
                        logger.warn(error);
                        handler(error.request.status, error);
                        return;
                    }
                }

                if (option.defaultHandler != null) {
                    logger.warn(error);
                    option.defaultHandler(error);
                } else {
                    logger.error(error);
                    addToast(errorToastOptions);
                }
            }
        },
    };
}

export function displayInfoCodeToast(infoCode: string, logger: Logger<string>): void {
    const { errorToastOptions, addToast } = useGlobalToast();

    const mappings: Record<string, ToastMapping> = {
        [UpdateCartResponseErrorCodes.UnknownPostalCode]: {
            summary: 'ERROR.CHANGE_REGION.SUMMARY',
            detail: 'ERROR.CHANGE_REGION.DETAIL',
        },
        [UpdateCartResponseErrorCodes.InvalidRentalDuration]: {
            summary: 'ERROR.CHANGE_RENTAL_DURATION.SUMMARY',
            detail: 'ERROR.CHANGE_RENTAL_DURATION.DETAIL',
        },
        // CompleteCartResponseErrorCodes.PaymentMethodNotAllowed
        [UpdateCartResponseErrorCodes.PaymentMethodNotAllowed]: {
            summary: 'ERROR.PAYMENT_METHOD_NOT_ALLOWED.SUMMARY',
            detail: 'ERROR.PAYMENT_METHOD_NOT_ALLOWED.DETAIL',
        },
        [CompleteCartResponseErrorCodes.InvalidCartState]: {
            summary: 'ERROR.CART_INVALID_STATE.SUMMARY',
            detail: 'ERROR.CART_INVALID_STATE.DETAIL',
        },
        [CompleteCartResponseErrorCodes.CartWithoutPrice]: {
            summary: 'ERROR.CART_WITHOUT_PRICE.SUMMARY',
            detail: 'ERROR.CART_WITHOUT_PRICE.DETAIL',
        },
        [CompleteCartResponseErrorCodes.NoCustomer]: {
            summary: 'ERROR.CUSTOMER_NOT_FOUND.SUMMARY',
            detail: 'ERROR.CUSTOMER_NOT_FOUND.DETAIL',
        },
        [CompleteCartResponseErrorCodes.NoBillingAddress]: {
            summary: 'ERROR.BILLING_ADDRESS_NOT_FOUND.SUMMARY',
            detail: 'ERROR.BILLING_ADDRESS_NOT_FOUND.DETAIL',
        },
        // OfferCompletionResponseErrorCodes.MissingTOS
        [CompleteCartResponseErrorCodes.MissingTOS]: {
            summary: 'ERROR.MISSING_TOS.SUMMARY',
            detail: 'ERROR.MISSING_TOS.DETAIL',
        },
        [OfferCompletionResponseErrorCodes.NoItemsSelected]: {
            summary: 'ERROR.OFFER_TO_ORDER_NO_ITEMS_SELECTED.SUMMARY',
            detail: 'ERROR.OFFER_TO_ORDER_NO_ITEMS_SELECTED.DETAIL',
        },
        [OfferCompletionResponseErrorCodes.OfferCompleted]: {
            summary: 'ERROR.OFFER_TO_ORDER_OFFER_COMPLETED.SUMMARY',
            detail: 'ERROR.OFFER_TO_ORDER_OFFER_COMPLETED.DETAIL',
        },
        [OfferCompletionResponseErrorCodes.OfferDeclined]: {
            summary: 'ERROR.OFFER_TO_ORDER_OFFER_DECLINED.SUMMARY',
            detail: 'ERROR.OFFER_TO_ORDER_OFFER_DECLINED.DETAIL',
        },
        [OfferCompletionResponseErrorCodes.OfferNotFound]: {
            summary: 'ERROR.OFFER_TO_ORDER_OFFER_NOT_FOUND.SUMMARY',
            detail: 'ERROR.OFFER_TO_ORDER_OFFER_NOT_FOUND.DETAIL',
        },
        [RequestChangeResponseErrorCodes.OfferNotFound]: {
            summary: 'ERROR.CHANGE_REQUEST_OFFER_NOT_FOUND.SUMMARY',
            detail: 'ERROR.CHANGE_REQUEST_OFFER_NOT_FOUND.DETAIL',
        },
        [RequestChangeResponseErrorCodes.ItemUnknown]: {
            summary: 'ERROR.CHANGE_REQUEST_ITEM_UNKNOWN.SUMMARY',
            detail: 'ERROR.CHANGE_REQUEST_ITEM_UNKNOWN.DETAIL',
        },
        [RequestChangeResponseErrorCodes.InvalidFileSize]: {
            summary: 'ERROR.CHANGE_REQUEST_INVALID_FILE_SIZE.SUMMARY',
            detail: 'ERROR.CHANGE_REQUEST_INVALID_FILE_SIZE.DETAIL',
            detailValues: { maxSize: MAX_FILE_SIZE_MB.toFixed(0) },
        },
        [RequestChangeResponseErrorCodes.InvalidFileExtension]: {
            summary: 'ERROR.CHANGE_REQUEST_INVALID_FILE_EXTENSION.SUMMARY',
            detail: 'ERROR.CHANGE_REQUEST_INVALID_FILE_EXTENSION.DETAIL',
            detailValues: { allowedExtensions: ALLOWED_EXTENSIONS.join(', ') },
        },
        [RequestChangeResponseErrorCodes.InvalidFileMimeType]: {
            summary: 'ERROR.CHANGE_REQUEST_INVALID_FILE_MIMETYPE.SUMMARY',
            detail: 'ERROR.CHANGE_REQUEST_INVALID_FILE_MIMETYPE.DETAIL',
            detailValues: { allowedMimeTypes: Object.keys(ALLOWED_MIMES_EXT).join(', ') },
        },
    };

    const info = mappings[infoCode];

    if (info != null) {
        addToast({
            ...errorToastOptions,
            summary: info.summary,
            detail: info.detail,
            summaryValues: info.summaryValues,
            detailValues: info.detailValues,
        });
    } else {
        logger.warn(`unknown infoCode: ${infoCode}`);
        addToast({
            ...errorToastOptions,
            summary: 'ERROR.DEFAULT_SUMMARY',
            detail: 'ERROR.DEFAULT_DETAIL',
        });
    }
}
